import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { getAppVersion, compatVersion, liveUpdateNumber } from '@/utils/app-version';

const INITIAL_STATE = {
  isLoading: false,
  middleNavIcons: [],
  featuredCards: [],
  justForYou: [],
  allAnimations: [],
  eventBanners: [],
  // Set when the member's course entitlements change. The featured card list is
  // filtered per member on the server, so a card unlocked mid-session is missing
  // from what was fetched at boot — and nothing else re-fetches this feed until
  // the app is relaunched. Cleared by the next successful fetch.
  featuredCardsStale: false,
  // Bumped every time the flag is raised. A fetch captures this before its
  // request goes out and clears the flag only if it has not moved since —
  // otherwise a request that started BEFORE an entitlement change would clear a
  // flag raised for data it never saw. Concurrent callers are deduplicated
  // through `ongoingRequests`, so the member returning to the homepage in that
  // window joins the OLD request, and the newly unlocked card would then stay
  // missing until relaunch.
  featuredCardsStaleSeq: 0,
};

const state = () => INITIAL_STATE;

const mutations = {
  setIsLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
  setMiddleNavIcons(state, middleNavIcons) {
    state.middleNavIcons = middleNavIcons;
  },
  setFeaturedCards(state, featuredCards) {
    state.featuredCards = featuredCards;
  },
  setJustForYou(state, justForYou) {
    state.justForYou = justForYou;
  },
  setAllAnimations(state, allAnimations) {
    state.allAnimations = allAnimations;
  },
  setEventBanners(state, eventBanners) {
    state.eventBanners = eventBanners;
  },
  markFeaturedCardsStale(state) {
    state.featuredCardsStale = true;
    state.featuredCardsStaleSeq += 1;
  },
  // Clears only if no invalidation landed since `seq` was captured.
  clearFeaturedCardsStale(state, seq) {
    if (seq !== state.featuredCardsStaleSeq) return;
    state.featuredCardsStale = false;
  },
};

const getters = {
  getCurrentAnimation(state) {
    if (
      !state.allAnimations ||
      !Array.isArray(state.allAnimations) ||
      state.allAnimations.length === 0
    ) {
      return null;
    }
    return state.allAnimations.find(animation => animation?.isActive) || null;
  },
  hasAnimations(state) {
    return (
      state.allAnimations && Array.isArray(state.allAnimations) && state.allAnimations.length > 0
    );
  },
};

// Track ongoing requests to prevent duplicates
const ongoingRequests = {
  getMiddleNavIcons: null,
  getFeaturedCards: null,
  getFeaturedCardsSeeAll: null,
  getJustForYou: null,
  getAllAnimations: null,
  getEventBanners: null,
};

let featuredFetchGen = 0;

const CACHE_KEYS = {
  middleNavIcons: 'hogc_middleNavIcons',
  featuredCards: 'hogc_featuredCards',
  allAnimations: 'hogc_allAnimations',
  eventBanners: 'hogc_eventBanners',
};

function readCache(key) {
  try {
    const cached = localStorage.getItem(key);
    return cached ? JSON.parse(cached) : null;
  } catch (_) {
    return null;
  }
}

function writeCache(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (_) {}
}

// Animation cache uses a versioned envelope + short TTL so a stale payload
// cannot keep a user pinned to an outdated active animation once the CMS
// switches to a new one. v2: the API filters per member from then on, so a v1
// payload may hold animations this member is not allowed to see.
const ANIMATIONS_CACHE_VERSION = 2;
const ANIMATIONS_CACHE_TTL_MS = 5 * 60 * 1000;

function readAnimationsCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEYS.allAnimations);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.__v !== ANIMATIONS_CACHE_VERSION || typeof parsed.t !== 'number') {
      return null;
    }
    if (Date.now() - parsed.t > ANIMATIONS_CACHE_TTL_MS) {
      return null;
    }
    return parsed.data;
  } catch (_) {
    return null;
  }
}

// The API filters animations per member (pastoral status, app version) and
// returns them highest priority first; every consumer takes the first active
// match, so the order is what decides which one a member sees. Re-sorted here so
// that does not rest on the response alone. It runs before the cache write, so a
// cached payload is already in order. Array#sort is stable, so equal priorities
// keep the API's order.
//
// One that needs a newer app (`updateRequired`) goes after every one this app
// can run, whatever its priority: a member on an older app sees the next
// animation they can use, and the Update app lock only when there is none.
function byPriority(animations) {
  if (!Array.isArray(animations)) return animations;
  const locked = animation => (animation?.updateRequired ? 1 : 0);
  return [...animations].sort(
    (a, b) => locked(a) - locked(b) || (b?.priority ?? 0) - (a?.priority ?? 0),
  );
}

function writeAnimationsCache(data) {
  try {
    localStorage.setItem(
      CACHE_KEYS.allAnimations,
      JSON.stringify({ __v: ANIMATIONS_CACHE_VERSION, t: Date.now(), data }),
    );
  } catch (_) {}
}

const actions = {
  async getMiddleNavIcons({ commit, dispatch, rootState }) {
    // Populate from cache instantly (stale-while-revalidate)
    const cached = readCache(CACHE_KEYS.middleNavIcons);
    if (cached) commit('setMiddleNavIcons', cached);

    // Check if request is already in progress
    if (ongoingRequests.getMiddleNavIcons) {
      return ongoingRequests.getMiddleNavIcons;
    }

    try {
      commit('setIsLoading', true);
      const isPreviewRoute =
        typeof window !== 'undefined' && window.location.pathname.startsWith('/preview/');
      if (isPreviewRoute) {
        console.warn(
          '[Homepage] Preview route detected - skipping middle nav icons API (requires auth).',
        );
        commit('setMiddleNavIcons', []);
        commit('setIsLoading', false);
        return;
      }
      const accessToken = rootState.user.token;

      // Store the promise to prevent duplicate calls
      const requestPromise = (async () => {
        const { status, data } = await axios.get(
          process.env.VUE_APP_API_URL + '/homepage/middle-nav-icons',
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            timeout: 20000, // 20 second timeout - longer than wrapper timeout to let wrapper handle it
          },
        );
        if (status !== 200) throw new Error('Failed in getting middle nav icons');
        commit('setMiddleNavIcons', data);
        writeCache(CACHE_KEYS.middleNavIcons, data);
        commit('setIsLoading', false);
        return data;
      })();

      ongoingRequests.getMiddleNavIcons = requestPromise;
      await requestPromise;
    } catch (e) {
      console.error('[Homepage] getMiddleNavIcons failed:', e);
      dispatch('toast/error', 'Failed to get middle nav icons.', {
        root: true,
      });
      throw e; // Re-throw to allow error handling upstream
    } finally {
      ongoingRequests.getMiddleNavIcons = null;
    }
  },
  async getFeaturedCards({ commit, dispatch, rootState, state }, payload = {}) {
    const seeAll = payload?.seeAll === true;

    // Populate from cache instantly (stale-while-revalidate).
    // See-all is a one-shot full list — do not overwrite the targeted cache.
    if (!seeAll) {
      const cached = readCache(CACHE_KEYS.featuredCards);
      if (cached) commit('setFeaturedCards', cached);
    }

    // Check if request is already in progress.
    const requestKey = seeAll ? 'getFeaturedCardsSeeAll' : 'getFeaturedCards';
    if (ongoingRequests[requestKey]) {
      return ongoingRequests[requestKey];
    }

    const fetchGen = ++featuredFetchGen;

    try {
      if (!seeAll) commit('setIsLoading', true);
      const accessToken = rootState.user.token;
      // Captured before the request goes out: what this response is allowed to
      // mark fresh (see featuredCardsStaleSeq).
      //
      // `state` MUST stay in this action's destructured context. Drop it and the
      // name still resolves — to `const state = () => INITIAL_STATE` at the top of
      // this file — so `staleSeq` becomes a property read on a FUNCTION, i.e.
      // undefined, and clearFeaturedCardsStale's `seq !== ...` guard is true for
      // ever. No error, no warning; the flag simply never clears again.
      const staleSeq = state.featuredCardsStaleSeq;

      // Store the promise to prevent duplicate calls
      const requestPromise = (async () => {
        const { status, data } = await axios.get(
          process.env.VUE_APP_API_URL + '/homepage/featured',
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            params: seeAll ? { seeAll: true } : undefined,
            timeout: 20000, // 20 second timeout - longer than wrapper timeout to let wrapper handle it
          },
        );
        if (status !== 200) throw new Error('Failed in getting featured cards');
        if (fetchGen !== featuredFetchGen) return data;
        commit('setFeaturedCards', data);
        commit('clearFeaturedCardsStale', staleSeq);
        if (!seeAll) writeCache(CACHE_KEYS.featuredCards, data);
        return data;
      })();

      ongoingRequests[requestKey] = requestPromise;
      await requestPromise;
    } catch (e) {
      console.error('[Homepage] getFeaturedCards failed:', e);
      dispatch('toast/error', 'Failed to get featured cards.', {
        root: true,
      });
      throw e; // Re-throw to allow error handling upstream
    } finally {
      ongoingRequests[requestKey] = null;
      if (!seeAll) commit('setIsLoading', false);
    }
  },
  async getJustForYou({ commit, dispatch, rootState, state }) {
    // Check if data already exists
    if (state.justForYou && Array.isArray(state.justForYou) && state.justForYou.length > 0) {
      return;
    }

    // Check if request is already in progress
    if (ongoingRequests.getJustForYou) {
      return ongoingRequests.getJustForYou;
    }

    try {
      commit('setIsLoading', true);
      const accessToken = rootState.user.token;

      // Store the promise to prevent duplicate calls
      const requestPromise = (async () => {
        const { status, data } = await axios.get(
          process.env.VUE_APP_API_URL + '/homepage/just-for-you',
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            timeout: 20000, // 20 second timeout - longer than wrapper timeout to let wrapper handle it
          },
        );
        if (status !== 200) throw new Error('Failed in getting just for you');
        commit('setJustForYou', data);
        commit('setIsLoading', false);
        return data;
      })();

      ongoingRequests.getJustForYou = requestPromise;
      await requestPromise;
    } catch (e) {
      console.error('[Homepage] getJustForYou failed:', e);
      dispatch('toast/error', 'Failed to get just for you.', {
        root: true,
      });
      throw e; // Re-throw to allow error handling upstream
    } finally {
      ongoingRequests.getJustForYou = null;
    }
  },
  async getAllAnimations({ commit, dispatch, rootState }) {
    // Populate from cache instantly (stale-while-revalidate).
    // Animations cache is TTL-gated so a stale payload cannot keep a user
    // pinned to a now-inactive animation (e.g. gachapon after a CMS switch).
    const cached = readAnimationsCache();
    if (cached) commit('setAllAnimations', cached);

    // Check if request is already in progress
    if (ongoingRequests.getAllAnimations) {
      return ongoingRequests.getAllAnimations;
    }

    try {
      commit('setIsLoading', true);
      const accessToken = rootState.user.token;

      // Store the promise to prevent duplicate calls
      const requestPromise = (async () => {
        // Same version force update checks: the store binary's, so "Update app"
        // is something the member can actually do. VUE_APP_FORCE_UPDATE_VERSION
        // overrides it for testing, as it does there.
        const { nativeVersion, bundleVersion } = await getAppVersion();
        const appVersion = process.env.VUE_APP_FORCE_UPDATE_VERSION || nativeVersion;
        const appUpdate = liveUpdateNumber(bundleVersion);
        const { status, data } = await axios.get(
          process.env.VUE_APP_API_URL + '/animation/get-animations',
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              // An animation can require a minimum version; a build that does
              // not send this is treated as older than every minimum.
              'x-app-version': compatVersion(appVersion),
              // ...and the live update on that binary, so a minimum can be
              // `1.3.7.3`. A separate header, so an API that predates it still
              // reads the version above.
              ...(appUpdate && { 'x-app-update': appUpdate }),
              // Picks the store listing for the "Update app" prompt.
              'x-app-platform': Capacitor.getPlatform(),
            },
            timeout: 20000, // 20 second timeout - longer than wrapper timeout to let wrapper handle it
          },
        );
        if (status !== 200) throw new Error('Failed in getting animations');
        const animations = byPriority(data);
        commit('setAllAnimations', animations);
        writeAnimationsCache(animations);
        commit('setIsLoading', false);
        return animations;
      })();

      ongoingRequests.getAllAnimations = requestPromise;
      await requestPromise;
    } catch (e) {
      console.error('[Homepage] getAllAnimations failed:', e);
      dispatch('toast/error', 'Failed to get animations.', {
        root: true,
      });
      throw e; // Re-throw to allow error handling upstream
    } finally {
      ongoingRequests.getAllAnimations = null;
    }
  },
  async getEventBanners({ commit, dispatch, rootState }) {
    // Populate from cache instantly (stale-while-revalidate)
    const cached = readCache(CACHE_KEYS.eventBanners);
    if (cached) commit('setEventBanners', cached);

    // Check if request is already in progress
    if (ongoingRequests.getEventBanners) {
      return ongoingRequests.getEventBanners;
    }

    try {
      commit('setIsLoading', true);
      const isPreviewRoute =
        typeof window !== 'undefined' && window.location.pathname.startsWith('/preview/');
      if (isPreviewRoute) {
        commit('setEventBanners', []);
        commit('setIsLoading', false);
        return;
      }
      const accessToken = rootState.user.token;

      // Store the promise to prevent duplicate calls
      const requestPromise = (async () => {
        const { status, data } = await axios.get(
          process.env.VUE_APP_API_URL + '/homepage/event-banners',
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            timeout: 20000,
          },
        );
        if (status !== 200) throw new Error('Failed in getting event banners');
        commit('setEventBanners', data);
        writeCache(CACHE_KEYS.eventBanners, data);
        commit('setIsLoading', false);
        return data;
      })();

      ongoingRequests.getEventBanners = requestPromise;
      await requestPromise;
    } catch (e) {
      console.error('[Homepage] getEventBanners failed:', e);
      // Silent fail - banners are non-critical, don't show error toast
    } finally {
      ongoingRequests.getEventBanners = null;
    }
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
